import React from 'react';
import { Account } from '../types';
import { FiAlertTriangle, FiCheckCircle, FiClock, FiCopy, FiEdit2, FiFileText } from 'react-icons/fi';
import CurrencyDisplay from './AmountDisplay';
import AccountIcon from './AccountIcon';
import Badge from './Badge';

interface AccountCardProps {
  account: Account;
  handleCopyAccountNumber: (accountNumber: string) => Promise<void>;
  showBalance?: boolean;
  onEditBalance?: () => void;
  showEditBalance?: boolean;
  showCreditCardBillBar?: boolean;
}

const getAccountTypeLabel = (type: Account['type']) => {
  return type.split('_').map(word =>
    word.charAt(0) + word.slice(1).toLowerCase()
  ).join(' ');
};

const maskAccountNumber = (accountNumber: string) => {
  const digitsOnly = accountNumber.replace(/\D/g, '');
  if (digitsOnly.length <= 4) return digitsOnly;
  const masked = '●'.repeat(digitsOnly.length - 4) + digitsOnly.slice(-4);
  const reversed = masked.split('').reverse().join('');
  const grouped = reversed.match(/.{1,4}/g)?.join(' ') ?? reversed;
  return grouped.split('').reverse().join('');
};

const renderMaskedAccountNumber = (accountNumber: string) => {
  const masked = maskAccountNumber(accountNumber);
  return masked.split('').map((char, index) =>
    char === '●' ? (
      <span
        key={`mask-${index}`}
        className="text-muted-foreground/70 text-[0.5em]"
      >
        {char}
      </span>
    ) : (
      char
    )
  );
};

const formatShortDate = (date?: string | null) => {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const formatShortMonthDay = (date?: string | null) => {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const addMonthsToLocalDateString = (date?: string | null, months = 1) => {
  const parsed = date ? new Date(`${date}T00:00:00`) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  parsed.setMonth(parsed.getMonth() + months);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const daysUntil = (date?: string | null) => {
  const parsed = date ? new Date(`${date}T00:00:00`) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((parsed.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};

const daysPast = (date?: string | null) => {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - parsed.getTime()) / (24 * 60 * 60 * 1000)));
};

const formatAmount = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
};

const AccountCard: React.FC<AccountCardProps> = ({
  account,
  handleCopyAccountNumber,
  showBalance = true,
  onEditBalance,
  showEditBalance = false,
  showCreditCardBillBar = true
}) => {
  const bill = account.type === 'CREDIT_CARD' ? account.creditCardBill : null;
  const billAmount = bill?.billAmount ?? 0;
  const paidAmount = bill?.paidAmount ?? 0;
  const pendingBillAmount = Math.max(0, billAmount - paidAmount);
  const nextStatementDateValue = addMonthsToLocalDateString(bill?.statementDate);
  const nextStatementDate = formatShortDate(nextStatementDateValue);
  const paidDate = formatShortDate(bill?.paidOnDate);
  const daysUntilDueDate = daysUntil(bill?.dueDate);
  const shouldShowPaidStatus = bill?.status === 'PAID' && daysUntilDueDate != null && daysUntilDueDate >= -7;
  const dueDate = formatShortMonthDay(bill?.dueDate);
  const overdueDays = daysPast(bill?.dueDate);
  const shouldShowCreditCardBar = showCreditCardBillBar && account.type === 'CREDIT_CARD';
  const billBar = (() => {
    if (!shouldShowCreditCardBar) return null;
    const daysUntilDue = daysUntil(bill?.dueDate);
    const daysUntilStatement = daysUntil(nextStatementDateValue);
    const isDueSoon = bill?.status !== 'PAID' && daysUntilDue != null && daysUntilDue >= 0 && daysUntilDue <= 1;
    const isStatementMissing = bill?.status === 'PAID' && daysUntilStatement != null && daysUntilStatement < 0;
    const amberClassName = 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300';

    if (!bill) {
      return {
        Icon: FiClock,
        className: 'bg-muted text-muted-foreground dark:bg-zinc-800 dark:text-zinc-400',
        text: `Awaiting statement`,
      };
    }
    if (bill.status === 'OVERDUE') {
      const daysText = overdueDays === 1 ? '1 day ago' : `${overdueDays ?? 0} days ago`;
      return {
        Icon: FiAlertTriangle,
        className: 'bg-red-100 text-red-500 dark:bg-red-950/60 dark:text-red-400',
        text: `${formatAmount(pendingBillAmount)} due ${daysText}`,
      };
    }
    if (isDueSoon) {
      const dueText = daysUntilDue === 0 ? 'today' : 'tomorrow';
      return {
        Icon: FiAlertTriangle,
        className: amberClassName,
        text: `${formatAmount(pendingBillAmount)} due ${dueText}`,
      };
    }
    if (bill.status === 'PAID') {
      if (isStatementMissing) {
        return {
          Icon: FiAlertTriangle,
          className: amberClassName,
          text: nextStatementDate ? `Statement not received since ${nextStatementDate}` : 'Statement not received',
        };
      }
      if (shouldShowPaidStatus) {
        return {
          Icon: FiCheckCircle,
          className: 'bg-muted text-muted-foreground dark:bg-zinc-800 dark:text-zinc-400',
          text: paidDate ? `Paid on ${paidDate}` : 'Bill paid',
        };
      }
      return {
        Icon: FiFileText,
        className: 'bg-muted text-muted-foreground dark:bg-zinc-800 dark:text-zinc-400',
        text: nextStatementDate ? `Statement arrives ${nextStatementDate}` : 'Awaiting statement',
      };
    }
    return {
      Icon: FiFileText,
      className: 'bg-muted text-muted-foreground dark:bg-zinc-800 dark:text-zinc-400',
      text: dueDate ? `${formatAmount(pendingBillAmount)} due on ${dueDate}` : `${formatAmount(pendingBillAmount)} due`,
    };
  })();

  return (
    <div
      className={`${billBar ? 'px-4 pt-4 pb-0 min-h-[10rem]' : 'p-4 min-h-[9rem] justify-center'} bg-secondary h-full flex flex-col hover:bg-secondary/90 transition-colors`}
    >
      {/* Top row: Logo/Balance */}
      <div className="flex justify-between items-center">
        <AccountIcon account={account} className="w-6 h-6" />
        {showBalance && (
          <div className="flex items-center space-x-1">
            <CurrencyDisplay
              amount={account.balance}
              className="font-semibold text-right text-lg"
              showOnlyNegative={true}
            />
            {showEditBalance && onEditBalance && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditBalance();
                }}
                className="p-1 text-muted-foreground hover:text-primary transition-colors"
                title="Edit balance"
              >
                <FiEdit2 className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </div>
      {/* Middle row: Name/Type */}
      <div className="flex justify-between items-center mt-3 gap-2 min-w-0">
        <h2 className="font-bold text-foreground text-xl truncate">
          {account.name}
        </h2>
        <Badge className="whitespace-nowrap">
          {getAccountTypeLabel(account.type)}
        </Badge>
      </div>
      {/* Bottom row: Number/Copy */}
      <div className="flex flex-col items-start mt-2">
        <p className="text-muted-foreground text-xs uppercase truncate">
          Account Number
        </p>
        <div className="flex items-center space-x-2">
          <p className="text-foreground text-sm whitespace-nowrap">
            {renderMaskedAccountNumber(account.accountNumber)}
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopyAccountNumber(account.accountNumber);
            }}
            className="text-muted-foreground hover:text-primary focus:outline-none"
            title="Copy account number"
          >
            <FiCopy className="h-4 w-4" />
          </button>
        </div>
      </div>
      {billBar && (
        <div className={`-mx-4 mt-auto flex min-h-[2rem] border-t border-border items-center justify-between gap-2.5 px-4 text-xs font-medium ${billBar.className}`}>
          <billBar.Icon className="h-4 w-4 shrink-0" />
          <span className="min-w-0 truncate text-right">
            {billBar.text}
          </span>
        </div>
      )}
    </div>
  );
};

export default AccountCard; 
